import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/auth/use-auth";
import { Button } from "@/components/ui/button";
import { useViewport } from "@/lib/viewport";

import { useOnboardingPrefs } from "./onboardingStore";

/**
 * First visit of the home page (T11-47): three short tips pointing at the big buttons, search
 * and the to-do bell. Not modal — nothing is dimmed or blocked, the tip is placed where it covers
 * neither the thing it points at nor a main button; Esc / "Skip" end it for good (Help → "Show
 * the tour again" resets it). On a phone (< 768 px) the same three tips are a short list instead.
 * No animation when the device asks for reduced motion.
 */
export const COACH_STEPS = [
  { key: "big_actions", selector: '[data-slot="big-actions"]' },
  { key: "search", selector: '[data-slot="search-button"]' },
  { key: "inbox", selector: '[data-slot="inbox-bell"]' },
] as const;

type StepKey = (typeof COACH_STEPS)[number]["key"];

/** Things a tip must never cover (the page's main buttons). */
const AVOID =
  '[data-slot="primary-action"], [data-slot="big-actions"] a, [data-slot="big-actions"] button';
const GAP = 12;
const MARGIN = 8;

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function overlaps(a: Rect, b: Rect): boolean {
  return (
    a.left < b.left + b.width &&
    b.left < a.left + a.width &&
    a.top < b.top + b.height &&
    b.top < a.top + a.height
  );
}

/** Where the tip goes: next to the target, inside the window, over nothing it must not hide. */
export function placeTip(
  target: Rect,
  tip: { width: number; height: number },
  view: { width: number; height: number },
  avoid: readonly Rect[],
): { top: number; left: number } {
  const clampLeft = (left: number) =>
    Math.min(Math.max(MARGIN, left), view.width - tip.width - MARGIN);
  const candidates = [
    { top: target.top + target.height + GAP, left: clampLeft(target.left) },
    { top: target.top - tip.height - GAP, left: clampLeft(target.left) },
    {
      top: target.top + target.height + GAP,
      left: clampLeft(target.left + target.width - tip.width),
    },
    { top: target.top, left: target.left + target.width + GAP },
    { top: target.top, left: target.left - tip.width - GAP },
    { top: view.height - tip.height - MARGIN, left: view.width - tip.width - MARGIN },
    { top: view.height - tip.height - MARGIN, left: MARGIN },
  ];
  const fits = (c: { top: number; left: number }) =>
    c.top >= MARGIN &&
    c.left >= MARGIN &&
    c.top + tip.height <= view.height - MARGIN &&
    c.left + tip.width <= view.width - MARGIN;
  const box = (c: { top: number; left: number }): Rect => ({ ...c, ...tip });
  const clear = (c: { top: number; left: number }) =>
    !overlaps(box(c), target) && avoid.every((r) => !overlaps(box(c), r));
  const best =
    candidates.find((c) => fits(c) && clear(c)) ??
    candidates.find((c) => fits(c) && !overlaps(box(c), target)) ??
    candidates[candidates.length - 1];
  return best ?? { top: MARGIN, left: MARGIN };
}

function rectOf(element: Element): Rect {
  const r = element.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

export function CoachMarks() {
  const { session } = useAuth();
  const viewport = useViewport();
  const [prefs, update] = useOnboardingPrefs(session?.user.id);
  const finish = useCallback(() => {
    update({ coachDone: true });
  }, [update]);
  if (prefs.coachDone) {
    return null;
  }
  return viewport === "phone" ? <CoachList onDone={finish} /> : <CoachTour onDone={finish} />;
}

function CoachList({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation("help");
  return (
    <section
      aria-labelledby="coach-list-title"
      data-slot="coach-list"
      className="flex flex-col gap-3 rounded-lg border bg-card p-4"
    >
      <h2 id="coach-list-title" className="font-bold">
        {t("coach.list_title")}
      </h2>
      <ul className="flex flex-col gap-2">
        {COACH_STEPS.map((step) => (
          <li key={step.key} className="flex flex-col">
            <span className="font-semibold">{t(`coach.${step.key}.title`)}</span>
            <span className="text-sm text-muted-foreground">{t(`coach.${step.key}.body`)}</span>
          </li>
        ))}
      </ul>
      <Button type="button" variant="outline" className="self-start" onClick={onDone}>
        {t("coach.done")}
      </Button>
    </section>
  );
}

function useTargets(): StepKey[] | null {
  const [found, setFound] = useState<StepKey[] | null>(null);
  useEffect(() => {
    let frame = 0;
    const started = performance.now();
    const look = () => {
      const present = COACH_STEPS.filter((s) => document.querySelector(s.selector)).map(
        (s) => s.key,
      );
      // wait (≤ 2 s) until the page has drawn its parts, then tour what is there
      if (present.length === COACH_STEPS.length || performance.now() - started > 2000) {
        setFound(present);
        return;
      }
      frame = requestAnimationFrame(look);
    };
    frame = requestAnimationFrame(look);
    return () => {
      cancelAnimationFrame(frame);
    };
  }, []);
  return found;
}

function CoachTour({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation("help");
  const steps = useTargets();
  const [index, setIndex] = useState(0);
  const [place, setPlace] = useState<{ target: Rect; top: number; left: number } | null>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const returnTo = useRef<Element | null>(null);
  const key = steps?.[index];
  const step = COACH_STEPS.find((s) => s.key === key);

  const end = useCallback(() => {
    onDone();
    if (returnTo.current instanceof HTMLElement) {
      returnTo.current.focus();
    }
  }, [onDone]);

  useLayoutEffect(() => {
    if (!step || !tipRef.current) {
      return;
    }
    const measure = () => {
      const target = document.querySelector(step.selector);
      const tip = tipRef.current;
      if (!target || !tip) {
        return;
      }
      const targetRect = rectOf(target);
      const avoid = [...document.querySelectorAll(AVOID)]
        .filter((el) => !target.contains(el) && !el.contains(target))
        .map(rectOf);
      const spot = placeTip(
        targetRect,
        { width: tip.offsetWidth, height: tip.offsetHeight },
        { width: window.innerWidth, height: window.innerHeight },
        avoid,
      );
      setPlace({ target: targetRect, ...spot });
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [step]);

  useEffect(() => {
    if (!step) {
      return;
    }
    returnTo.current ??= document.activeElement;
    nextRef.current?.focus({ preventScroll: true });
  }, [step]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        end();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [end]);

  if (!steps) {
    return null;
  }
  if (!step) {
    return steps.length === 0 ? <AutoDone onDone={onDone} /> : null;
  }
  const last = index === steps.length - 1;
  return createPortal(
    <>
      {place ? (
        <div
          aria-hidden
          data-slot="coach-ring"
          className="pointer-events-none fixed z-[70] rounded-lg ring-4 ring-[#2563eb] ring-offset-2"
          style={{
            top: place.target.top - 4,
            left: place.target.left - 4,
            width: place.target.width + 8,
            height: place.target.height + 8,
          }}
        />
      ) : null}
      <div
        ref={tipRef}
        role="dialog"
        aria-modal="false"
        aria-labelledby="coach-title"
        aria-describedby="coach-body"
        data-slot="coach-tip"
        data-step={step.key}
        className="fixed z-[71] flex w-80 max-w-[calc(100vw-16px)] flex-col gap-2 rounded-lg border-2 border-[#2563eb] bg-popover p-4 text-popover-foreground shadow-xl motion-safe:animate-in motion-safe:fade-in"
        style={
          place ? { top: place.top, left: place.left } : { top: 0, left: 0, visibility: "hidden" }
        }
      >
        <p className="text-xs font-medium text-muted-foreground">
          {t("coach.step", { n: index + 1, total: steps.length })}
        </p>
        <h2 id="coach-title" className="font-bold">
          {t(`coach.${step.key}.title`)}
        </h2>
        <p id="coach-body" className="text-sm">
          {t(`coach.${step.key}.body`)}
        </p>
        <div className="mt-1 flex items-center justify-between gap-2">
          <Button type="button" variant="ghost" onClick={end} data-action="coach-skip">
            {t("coach.skip")}
          </Button>
          <Button
            ref={nextRef}
            type="button"
            data-action="coach-next"
            onClick={() => {
              if (last) {
                end();
              } else {
                setPlace(null);
                setIndex((i) => i + 1);
              }
            }}
          >
            {last ? t("coach.done") : t("coach.next")}
          </Button>
        </div>
      </div>
    </>,
    document.body,
  );
}

function AutoDone({ onDone }: { onDone: () => void }) {
  useEffect(() => {
    onDone();
  }, [onDone]);
  return null;
}
